import { z } from 'zod';
import { Base64ImageSchema } from './contracts/service-gateway';

/**
 * Chat schemas - Only for cross-platform data (API calls, LLM requests, IPC messages)
 * Internal app state uses plain TypeScript types with Zustand
 */

// Vercel AI SDK compatible message schema (for LLM API calls)
export const AIMessageRoleSchema = z.enum([
  'system',
  'user',
  'assistant',
  'function',
  'data',
  'tool',
]);
export type AIMessageRole = z.infer<typeof AIMessageRoleSchema>;

// Vercel AI SDK ToolCall format (for LLM function calls)
export const AIToolCallSchema = z.object({
  id: z.string(),
  type: z.literal('function'),
  function: z.object({
    name: z.string(),
    arguments: z.string(), // JSON string
  }),
});

export type AIToolCall = z.infer<typeof AIToolCallSchema>;

// Core AI message schema (for LLM API requests/responses)
export const AIMessageSchema = z.object({
  id: z.string(),
  role: AIMessageRoleSchema,
  content: z.string(),
  name: z.string().optional(), // For function messages
  toolCalls: z.array(AIToolCallSchema).optional(),
  experimental_attachments: z
    .array(
      z.object({
        name: z.string().optional(),
        contentType: z.string().optional(),
        url: z.string(),
      })
    )
    .optional(),
});

export type AIMessage = z.infer<typeof AIMessageSchema>;

// Chat image attachment for IPC/API transmission
export const ChatImageAttachmentSchema = z.object({
  id: z.string(),
  name: z.string(),
  contentType: z.string(),
  dataUrl: z.string(), // Base64 encoded image data
  thumbnailUrl: z.string().optional(),
  timestamp: z.number(),
  metadata: z
    .object({
      sourceId: z.string().optional(),
      sourceName: z.string().optional(),
      displayBounds: z
        .object({
          x: z.number(),
          y: z.number(),
          width: z.number(),
          height: z.number(),
        })
        .optional(),
      scaleFactor: z.number().default(1).optional(),
    })
    .optional(),
  processingTime: z.number().optional(),
});

export type ChatImageAttachment = z.infer<typeof ChatImageAttachmentSchema>;

// Keep ScreenCapture for backward compatibility, but make it extend ChatImageAttachment
export const ScreenCaptureSchema = ChatImageAttachmentSchema;
export type ScreenCapture = ChatImageAttachment;

// Validation functions (only for boundary-crossing data)
export const validateAIMessage = (data: unknown): AIMessage => {
  return AIMessageSchema.parse(data);
};

export const validateChatImageAttachment = (data: unknown): ChatImageAttachment => {
  return ChatImageAttachmentSchema.parse(data);
};

export const validateScreenCapture = (data: unknown): ScreenCapture => {
  return ScreenCaptureSchema.parse(data);
};

export const validateChatMessage = (data: unknown): ChatMessage => {
  return ChatMessageSchema.parse(data);
};

export const validateChatSession = (data: unknown): ChatSession => {
  return ChatSessionSchema.parse(data);
};

export const validateOpenAIRequest = (data: unknown): OpenAIRequest => {
  return OpenAIRequestSchema.parse(data);
};

export const validateOpenAIResponse = (data: unknown): OpenAIResponse => {
  return OpenAIResponseSchema.parse(data);
};

export const validateChatServiceConfig = (data: unknown): ChatServiceConfig => {
  return ChatServiceConfigSchema.parse(data);
};

// Message status schema
export const MessageStatusSchema = z.enum([
  'sending',
  'sent',
  'error',
  'processing',
  'received',
  'streaming',
]);
export type MessageStatus = z.infer<typeof MessageStatusSchema>;

// Token usage schema
export const TokenUsageSchema = z.object({
  input: z.number().optional(),
  output: z.number().optional(),
  total: z.number().optional(),
});

// Error details schema
export const ErrorDetailsSchema = z.object({
  code: z.string(),
  message: z.string(),
  type: z.string().optional(),
  details: z.any().optional(),
});

// Usage statistics schema
export const UsageStatsSchema = z.object({
  promptTokens: z.number().optional(),
  completionTokens: z.number().optional(),
  totalTokens: z.number().optional(),
});

// Message metadata schema
export const MessageMetadataSchema = z.object({
  imageAttachment: ChatImageAttachmentSchema.optional(),
  screenCapture: ScreenCaptureSchema.optional(), // Keep for backward compatibility
  processingTime: z.number().optional(),
  retryCount: z.number().optional(),
  tokens: TokenUsageSchema.optional(),
  model: z.string().optional(),
  provider: z.string().optional(),
  error: ErrorDetailsSchema.optional(),
  finishReason: z.enum(['stop', 'length', 'tool_calls', 'content_filter', 'cancelled']).optional(),
  usage: UsageStatsSchema.optional(),
  hasVision: z.boolean().optional(),
});

// Chat message schema (extends AIMessage with local-specific fields)
export const ChatMessageSchema = AIMessageSchema.extend({
  status: MessageStatusSchema,
  timestamp: z.number(),
  metadata: MessageMetadataSchema.optional(),
  error: z.string().optional(),
  // Base64 images separated by type
  screenshots: z
    .array(Base64ImageSchema)
    .optional()
    .describe('Base64 screenshot data (silent, not shown in chat history)'),
  attachments: z
    .array(Base64ImageSchema)
    .optional()
    .describe('Base64 attachment data (shown in chat history)'),
});

export type ChatMessage = z.infer<typeof ChatMessageSchema>;

// Session status schema
export const SessionStatusSchema = z.enum(['active', 'idle', 'closed', 'error']);
export type SessionStatus = z.infer<typeof SessionStatusSchema>;

// Session metadata schema
export const SessionMetadataSchema = z.object({
  totalMessages: z.number(),
  avgResponseTime: z.number().optional(),
  totalTokens: z.number(),
  screenCaptures: z.number(),
  defaultPrompt: z.string().optional(),
  model: z.string().optional(),
  provider: z.string().optional(),
});

// Chat session schema
export const ChatSessionSchema = z.object({
  id: z.string(),
  title: z.string().optional(),
  createdAt: z.number(),
  lastActivityAt: z.number(),
  messages: z.array(ChatMessageSchema),
  status: SessionStatusSchema,
  metadata: SessionMetadataSchema,
});

export type ChatSession = z.infer<typeof ChatSessionSchema>;

// OpenAI-specific schemas for API integration
export const OpenAIMessageSchema = z.object({
  role: z.enum(['system', 'user', 'assistant']),
  content: z.union([
    z.string(),
    z.array(
      z.union([
        z.object({ type: z.literal('text'), text: z.string() }),
        z.object({
          type: z.literal('image_url'),
          image_url: z.object({
            url: z.string(),
            detail: z.enum(['low', 'high', 'auto']).optional(),
          }),
        }),
      ])
    ),
  ]),
});

export const OpenAIRequestSchema = z.object({
  model: z.string().default('gpt-4'),
  messages: z.array(OpenAIMessageSchema),
  stream: z.boolean().optional(),
  temperature: z.number().optional(),
  max_tokens: z.number().optional(),
});

export const OpenAIUsageSchema = z.object({
  prompt_tokens: z.number(),
  completion_tokens: z.number(),
  total_tokens: z.number(),
});

export const OpenAIResponseSchema = z.object({
  id: z.string(),
  object: z.string(),
  created: z.number(),
  model: z.string(),
  choices: z.array(
    z.object({
      index: z.number(),
      message: OpenAIMessageSchema,
      finish_reason: z.enum(['stop', 'length', 'content_filter', 'tool_calls']).nullable(),
    })
  ),
  usage: OpenAIUsageSchema,
});

export type OpenAIMessage = z.infer<typeof OpenAIMessageSchema>;
export type OpenAIRequest = z.infer<typeof OpenAIRequestSchema>;
export type OpenAIResponse = z.infer<typeof OpenAIResponseSchema>;
export type OpenAIUsage = z.infer<typeof OpenAIUsageSchema>;

// Chat service configuration
export const ChatServiceConfigSchema = z.object({
  apiKey: z.string().optional(),
  model: z.string().default('gpt-4'),
  temperature: z.number().min(0).max(2).default(0.7),
  maxTokens: z.number().positive().default(1000),
  streamEnabled: z.boolean().default(true),
});

export type ChatServiceConfig = z.infer<typeof ChatServiceConfigSchema>;
